import React from "react";
import {
  FlatList,
  StyleSheet,
  Text,
  TouchableHighlight,
  TouchableNativeFeedback,
  View,
} from "react-native";
import entityStyle from "@utils/entityStyle";
import { useSelector } from "react-redux";
import useLeaderState from "@hooks/useLeaderState";
import t from "@constants/localization";
import Caret from "../../assets/svg/Caret";
import { isCLApp } from "../../config";
import {
  CMS_ENTITY_TYPE,
  SHARING_EVENTS,
} from "../../constants/analyticsEventsCT";
import metrics from "../../constants/metrics";
import handleCMSNavigation from "../../utils/handleCMSNavigation";
import parseTemplate from "../../utils/parseTemplate";
import { shareOnWhatsapp } from "../../utils/shareEntity";
import AppText from "../AppText";
import CustomText from "../CustomText";
import VerticalProduct from "../ProductDisplayUnit/VerticalProduct";
import HorizontalOOS from "../ProductDisplayUnit/HorizontalOutOfStock";
import HorizontalProduct from "../ProductDisplayUnit/HorizontalProduct";
import ProductSkeleton from "./Skeleton/ProductSkeleton";
import WhatsAppShare from "./WhatsAppShare";

const PATH = "components/CMS/ProductList";

const ProductList = (props) => {
  const user = useSelector((state) => state?.auth?.user);

  const { canLeaderUseApp } = useLeaderState();

  const {
    cl_title,
    cx_title,
    products = [],
    cms_item_id = 0,
    link = "",
    link_type = "",
    sharingTemplate: { image_link: sharing_image_link, template = "" },
    sharingLink = "",
    shareSkuTemplates = "",
    trackEvent = () => {},
    horizontal,
  } = props;

  const onSeeAllPress = async () => {
    handleCMSNavigation(link, link_type);
    trackEvent(CMS_ENTITY_TYPE.PRODUCT_LIST, {
      cl_title,
      cx_title,
      cms_item_id,
    });
  };
  const { CL: { template: shareSkuTemplate = "" } = {} } =
    shareSkuTemplates || {};

  const renderPduItem = ({ item: product, index }) => {
    const { slug } = product;
    const { app_share_app_link } = user;
    const sharingLink = `${app_share_app_link}?app_screen=ProductDetails&slug=${slug}&channel=cl_product_share`;
    return horizontal ? (
      product.slug === "render_see_all" ? (
        renderSkuSeeAllCard()
      ) : product.product_status === "Active" ? (
        <HorizontalProduct
          key={`p${index}`}
          data={product}
          index={index}
          trackEvent={trackEvent}
          sharingLink={sharingLink}
          sharingTemplate={shareSkuTemplate}
          disabled={props.fromCart || props.routeName === "ConfirmOrder"}
          horizontal={true}
        />
      ) : (
        <HorizontalOOS
          key={`p${index}`}
          data={product}
          index={index}
          trackEvent={trackEvent}
          sharingTemplate={shareSkuTemplate}
          disabled={props.fromCart || props.routeName === "ConfirmOrder"}
          horizontal={true}
        />
      )
    ) : product.slug === "render_see_all" ? (
      <></>
    ) : (
      <VerticalProduct
        sharingLink={sharingLink}
        key={`p${index}`}
        data={product}
        index={index}
        trackEvent={trackEvent}
        sharingTemplate={shareSkuTemplate}
        disabled={props.fromCart || props.routeName === "ConfirmOrder"}
        horizontal={true}
      />
    );
  };

  const renderSkuSeeAllCard = () => {
    return props.fromCart || props.routeName === "ConfirmOrder" ? null : (
      <TouchableNativeFeedback onPress={onSeeAllPress}>
        <View
          style={{
            width: metrics.screenWidth / 2,
            borderColor: "#EEEEEE",
            borderWidth: 1,
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
          }}
        >
          <View
            style={{
              borderRadius: 100,
              borderWidth: 1,
              padding: 8,
              borderColor: "rgba(0,0,0,0.1)",
              backgroundColor: "white",
            }}
          >
            <Caret strokeColor="#E40046" />
          </View>
          <Text style={{ color: "#E40046", marginTop: 8, fontSize: 16 }}>
            {t(`${PATH}/viewAll`)}
          </Text>
        </View>
      </TouchableNativeFeedback>
    );
  };

  const renderSkuSeeAllBottomStrip = () => {
    if (horizontal || props.fromCart || props.routeName === "ConfirmOrder") {
      return null;
    } else {
      return (
        <TouchableHighlight
          style={[styles.beLikeRow, styles.viewAllProducts]}
          underlayColor="rgba(0, 0, 0, 0.05)"
          onPress={onSeeAllPress}
        >
          <>
            <AppText
              fontType="content1"
              style={{
                color: "#E40046",
              }}
              bold
            >
              {t(`${PATH}/viewAll`)}
            </AppText>
            <Caret strokeColor="#E40046" />
          </>
        </TouchableHighlight>
      );
    }
  };

  const titleText = isCLApp ? cl_title : cx_title;
  const isProductsEmpty = products.length === 0;
  if (isProductsEmpty) {
    return null;
  }

  return (
    <View
      style={[
        styles.container,
        {
          marginTop: entityStyle.entityContainer.marginTop,
          paddingBottom: 8,
        },
      ]}
    >
      <View
        style={[
          styles.beLikeRow,
          {
            justifyContent: "space-between",
            paddingHorizontal: 16,
            paddingVertical: 8,
            alignItems: "center",
          },
        ]}
      >
        {typeof titleText === "string" && titleText.trim().length ? (
          <View style={{ flex: 1 }}>
            <CustomText
              variant="entityHeading"
              style={{ marginBottom: 0, paddingHorizontal: 0 }}
            >
              {titleText}
            </CustomText>
          </View>
        ) : (
          <></>
        )}
        <View style={styles.beLikeRow}>
          <View style={{ paddingLeft: 20 }}>
            <WhatsAppShare
              eventName={SHARING_EVENTS.LIST_SHARE}
              eventProps={{
                screen_name: props.routeName,
                cms_item_id,
                cl_title,
                cx_title,
              }}
              title={t(`${PATH}/share`)}
              medium
              onShare={() => {
                if (!canLeaderUseApp()) {
                  return;
                }
                const message = parseTemplate(template, {});
                shareOnWhatsapp({
                  message: `${message}\n${sharingLink}`,
                  image: sharing_image_link,
                });
              }}
            />
          </View>
        </View>
      </View>
      <FlatList
        data={[...products, { slug: "render_see_all" }]}
        renderItem={renderPduItem}
        horizontal={horizontal}
        key={`${Math.round(products.length / 2)}`}
        keyExtractor={(item) => `${item.slug}`}
        ListEmptyComponent={ProductSkeleton}
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ paddingHorizontal: horizontal ? 15 : 0 }}
      />
      {renderSkuSeeAllBottomStrip()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "white",
  },
  beLikeRow: {
    flexDirection: "row",
  },
  viewAllProducts: {
    padding: 15,
    justifyContent: "center",
    alignItems: "center",
  },
});

export default ProductList;
